import type { StoreOrderState } from '@kenocyber/shared';

/**
 * How the store speaks to a player: labels, timing, and what an error means.
 *
 * Pure functions in their own module, for the same reason the reveal timeline
 * is: this is the part that can be asserted exactly, and pulling it out of the
 * component means the polling policy and the wording are testable without
 * rendering anything.
 *
 * ## `approved` is not "delivered"
 *
 * The provider confirming the money and Kenocyber handing over the Cash are two
 * different events, and the screen says so. Calling `approved` "compra
 * concluída" would mean a player who closed the tab in between would believe
 * they had something they did not yet have.
 */

export interface OrderPresentation {
  /** What the player reads. */
  readonly label: string;
  /** One line explaining what happens next, or nothing. */
  readonly hint: string | null;
  /**
   * A machine-readable tone, so the interface never depends on colour ALONE.
   * Every tone also carries an icon and a text label.
   */
  readonly tone: 'waiting' | 'working' | 'done' | 'failed' | 'review';
}

const PRESENTATION: Readonly<Record<StoreOrderState, OrderPresentation>> = Object.freeze({
  created: {
    label: 'Criando cobrança',
    hint: 'Gerando o código Pix. Isso leva alguns segundos.',
    tone: 'working',
  },
  pending: {
    label: 'Aguardando pagamento',
    hint: 'Pague o código Pix. A entrega é automática, não precisa avisar ninguém.',
    tone: 'waiting',
  },
  approved: {
    /*
     * The money is ours; the Cash is not in the account yet. Saying "concluída"
     * here would be a lie a player could act on.
     */
    label: 'Pagamento confirmado',
    hint: 'Entregando o benefício. Não feche até concluir — mas se fechar, a entrega acontece do mesmo jeito.',
    tone: 'working',
  },
  granted: {
    label: 'Compra concluída',
    hint: null,
    tone: 'done',
  },
  rejected: {
    label: 'Pagamento recusado',
    hint: 'O banco não concluiu o Pix. Nada foi cobrado.',
    tone: 'failed',
  },
  cancelled: {
    label: 'Cancelado',
    hint: 'A cobrança foi cancelada. Nada foi cobrado.',
    tone: 'failed',
  },
  expired: {
    label: 'Expirado',
    hint: 'O código venceu sem pagamento. Você pode gerar um novo.',
    tone: 'failed',
  },
  refund_pending: {
    label: 'Reembolso em andamento',
    hint: 'Estamos processando o estorno.',
    tone: 'working',
  },
  refunded: {
    label: 'Reembolsado',
    hint: 'O valor voltou para você e o benefício foi revertido.',
    tone: 'failed',
  },
  manual_review: {
    /*
     * Deliberately vague. The player needs to know it is being looked at; what
     * diverged is between us and the provider, and naming it would leak how the
     * checks work to somebody probing them.
     */
    label: 'Em análise',
    hint: 'Algo não bateu e uma pessoa vai verificar. Nada foi perdido.',
    tone: 'review',
  },
});

export function presentOrder(state: StoreOrderState): OrderPresentation {
  return PRESENTATION[state];
}

/**
 * States the screen still has something to wait for.
 *
 * The polling predicate AND the "can this be reopened" predicate, deliberately
 * the same list: if there is nothing left to happen, there is nothing to poll
 * and nothing to show a code for.
 */
const LIVE_STATES: readonly StoreOrderState[] = Object.freeze([
  'created',
  'pending',
  'approved',
  'refund_pending',
]);

export function isLiveOrder(state: StoreOrderState): boolean {
  return LIVE_STATES.includes(state);
}

/** The order is finished, one way or another. Nothing more will change. */
export function isFinishedOrder(state: StoreOrderState): boolean {
  return !isLiveOrder(state) && state !== 'manual_review';
}

/* -------------------------------------------------------------------------- */
/*  Polling                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Four seconds while things are normal.
 *
 * Inside the 3-5 s band the design asked for. Faster is pointless - a Pix
 * confirmation takes seconds to reach the provider and then us - and slower
 * makes a paid purchase feel broken.
 */
export const POLL_BASE_MS = 4_000;

/**
 * The ceiling a failing poll backs off to.
 *
 * Thirty seconds. Beyond that the screen stops feeling live; below it, an API
 * having a bad minute gets hammered by every open shop in the game.
 */
export const POLL_MAX_MS = 30_000;

/**
 * How long to wait before the next read.
 *
 * Doubling per consecutive failure, capped. `failures` resets to zero on any
 * successful read, so one blip does not slow the screen down for a minute.
 */
export function pollDelayMs(failures: number): number {
  if (failures <= 0) {
    return POLL_BASE_MS;
  }
  return Math.min(POLL_MAX_MS, POLL_BASE_MS * 2 ** failures);
}

/**
 * Should the screen be polling at all right now?
 *
 * A hidden tab does not poll. It is not a correctness matter - the server
 * reconciles whether or not anybody is looking - but a background tab quietly
 * asking every four seconds for an hour is a cost with no reader, and browsers
 * throttle those timers unpredictably anyway.
 */
export function shouldPoll(state: StoreOrderState, documentHidden: boolean): boolean {
  return isLiveOrder(state) && !documentHidden;
}

/* -------------------------------------------------------------------------- */
/*  Time left                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `mm:ss` until the charge expires, from a SERVER instant.
 *
 * The deadline comes from the order; only the countdown between renders is
 * local. A browser clock that is minutes off shows a slightly wrong countdown
 * and nothing else - the expiry itself is decided by the server, which asks the
 * provider before closing anything.
 */
export function countdownLabel(expiresAt: string | null, nowMs: number): string | null {
  if (!expiresAt) {
    return null;
  }
  const remaining = Date.parse(expiresAt) - nowMs;
  if (!Number.isFinite(remaining) || remaining <= 0) {
    return '00:00';
  }
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * What a product delivers, in words.
 *
 * VIP is stated in DAYS - `30 dias de VIP`, never `0 Cash`. A row that reported
 * the Cash field of a VIP purchase would be technically accurate and read as
 * though somebody had paid for nothing.
 */
export function benefitLabel(product: {
  kind: 'cash' | 'vip';
  cashAmount: string;
  vipSeconds: number;
}): string {
  if (product.kind === 'cash') {
    return `${product.cashAmount} Cash`;
  }
  const days = Math.floor(product.vipSeconds / 86_400);
  if (days > 0 && product.vipSeconds % 86_400 === 0) {
    return `${days} ${days === 1 ? 'dia' : 'dias'} de VIP`;
  }
  return `${durationLabel(product.vipSeconds)} de VIP`;
}

/** A VIP window as `7d 04h`, or `03h 12m`, or `08m`. Never a bare second count. */
export function durationLabel(totalSeconds: number): string {
  if (totalSeconds <= 0) {
    return '—';
  }
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, '0')}h`;
  }
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${String(minutes).padStart(2, '0')}m`;
}

/** A server ISO instant as `10/09/2026 12:30`. Never a raw string on screen. */
export function instantLabel(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString('pt-BR');
}

/* -------------------------------------------------------------------------- */
/*  The promotion's countdown                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The clock the promotional countdown runs on, and it is NOT the browser's.
 *
 * Three numbers, taken together at the moment the catalogue answered:
 *
 * - `serverNowMs` - what the SERVER said the time was;
 * - `endsAtMs` - when the server says the promotion ends;
 * - `anchorMs` - a MONOTONIC reading (`performance.now()`) at that same moment.
 *
 * Everything afterwards is measured as elapsed monotonic time since the anchor,
 * so the displayed remaining time is `endsAt - (serverNow + elapsed)`. The wall
 * clock is never read again.
 *
 * That is what makes changing the Windows clock useless: `performance.now()` is
 * a count since the document loaded and is unaffected by the system time
 * jumping forwards or backwards. Setting the machine to last month cannot
 * reopen a promotion, and setting it to next year cannot end one early - and
 * neither could matter anyway, because the PRICE is decided by the server. The
 * countdown is presentation; this only keeps the presentation honest.
 */
export interface PromotionClock {
  readonly serverNowMs: number;
  readonly endsAtMs: number;
  readonly anchorMs: number;
}

/**
 * Anchors a countdown to the server's answer, or null when there is nothing to
 * count down to.
 *
 * Null when no deadline is configured, and null for anything unparseable. It is
 * never guessed at: a countdown invented from a missing date would be a
 * deadline nobody set.
 */
export function promotionClockFrom(
  serverNow: string | null | undefined,
  endsAt: string | null | undefined,
  monotonicNowMs: number,
): PromotionClock | null {
  if (!serverNow || !endsAt) {
    return null;
  }
  const serverNowMs = Date.parse(serverNow);
  const endsAtMs = Date.parse(endsAt);
  if (!Number.isFinite(serverNowMs) || !Number.isFinite(endsAtMs)) {
    return null;
  }
  return { serverNowMs, endsAtMs, anchorMs: monotonicNowMs };
}

/** Milliseconds left, floored at zero. Measured from the anchor, not the clock. */
export function promotionRemainingMs(clock: PromotionClock, monotonicNowMs: number): number {
  const elapsed = Math.max(0, monotonicNowMs - clock.anchorMs);
  return Math.max(0, clock.endsAtMs - (clock.serverNowMs + elapsed));
}

/**
 * `3d 04:12:09`, or `04:12:09` inside the last day.
 *
 * Whole seconds, floored, and never negative - "faltam -5s" is not a thing. At
 * zero it reads `00:00:00`, which is what the last second before a refresh
 * should look like.
 */
export function promotionCountdownLabel(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const clock = [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
  return days > 0 ? `${days}d ${clock}` : clock;
}

/** `50% OFF`, from the SERVER's basis points. Null when there is no discount. */
export function discountLabel(basisPoints: number | null | undefined): string | null {
  if (typeof basisPoints !== 'number' || basisPoints <= 0) {
    return null;
  }
  // Integer basis points, like every rate in this codebase. 5000 -> 50.
  return `${Math.round(basisPoints / 100)}% OFF`;
}

/** What commercial phase an order was sold in. Null when it predates the field. */
export function phaseLabel(phase: string | null | undefined): string | null {
  if (phase === 'closed_alpha') {
    return 'Alpha fechado';
  }
  if (phase === 'post_alpha') {
    return 'Pós-Alpha';
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Errors                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What the player is told when something goes wrong.
 *
 * An allow-list keyed on the API's own machine reasons. Anything unrecognised
 * becomes one generic sentence - which is the point: a stack trace, a SQL
 * fragment, a table name, a token, a provider body or an internal URL must
 * never reach a screen, and the way to guarantee that is to never pass a
 * server-authored string through.
 */
const MESSAGES: Readonly<Record<string, string>> = Object.freeze({
  store_disabled: 'Os pagamentos ainda estão em configuração. Tente mais tarde.',
  unknown_product: 'Este pacote não está disponível no momento.',
  daily_limit_reached: 'Você atingiu o limite de compra das últimas 24 horas.',
  too_many_pending_orders:
    'Você já tem três cobranças aguardando pagamento. Pague ou deixe uma expirar.',
  rate_limited: 'Muitas tentativas seguidas. Aguarde um instante e tente de novo.',
  provider_unavailable:
    'Não foi possível gerar a cobrança agora. Tente novamente em alguns segundos.',
  idempotency_conflict: 'Esta tentativa já foi usada para outro pacote. Comece uma nova compra.',
  invalid_state: 'Este pedido não permite mais essa ação.',
  not_found: 'Pedido não encontrado.',
  unauthorized: 'Sua sessão expirou. Entre novamente para continuar.',
  /*
   * The two refusals the confirmation screen exists to make possible.
   *
   * Both mean the same structural thing - what the player was looking at is no
   * longer what would be charged or agreed to - and both are worded as an
   * instruction to LOOK AGAIN, because repeating the request automatically is
   * exactly what must not happen. The promotion ending while a confirmation sat
   * open is the ordinary way to reach the first.
   */
  price_changed: 'O preço foi atualizado. Revise o novo valor antes de confirmar.',
  terms_outdated: 'As regras do Alpha foram atualizadas. Leia e aceite novamente.',
});

/**
 * The refusals that invalidate an open confirmation.
 *
 * Named as a set because the panel treats them identically: close the dialog,
 * clear the acceptance, re-read the catalogue, and say why. What it must NEVER
 * do is retry - a repeat with the same key after a price change is a purchase
 * at a price nobody looked at.
 */
export function invalidatesConfirmation(reason: string | null | undefined): boolean {
  return reason === 'price_changed' || reason === 'terms_outdated';
}

/** The generic answer. Says what to do, and nothing about our internals. */
export const GENERIC_ERROR = 'Não foi possível concluir agora. Tente novamente em instantes.';

export function storeErrorMessage(reason: string | null | undefined, status?: number): string {
  if (status === 401) {
    return MESSAGES.unauthorized as string;
  }
  if (reason && reason in MESSAGES) {
    return MESSAGES[reason] as string;
  }
  return GENERIC_ERROR;
}

/**
 * May the player press the button again after this failure?
 *
 * Only where repeating is safe AND useful. A limit that has been reached will
 * still be reached a second later; a conflicting key will still conflict. A
 * network blip and an unavailable provider are worth retrying, and both are
 * safe because the attempt keeps its idempotency key.
 */
export function isRetryable(reason: string | null | undefined, status?: number): boolean {
  if (status === 401) {
    return false;
  }
  if (!reason) {
    // A transport failure: no answer at all. Retrying with the same key either
    // finds the order the lost response created, or creates it.
    return true;
  }
  return reason === 'provider_unavailable' || reason === 'rate_limited';
}
